<script setup>
import { ref, reactive, computed, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useMapGetter, useStore } from 'dashboard/composables/store';
import Button from 'dashboard/components-next/button/Button.vue';
import CloseCircleBeneficiaryPicker from 'dashboard/components-next/Contacts/CloseCircle/CloseCircleBeneficiaryPicker.vue';

const props = defineProps({
  project: {
    type: Object,
    required: true,
  },
});

const emit = defineEmits(['updated']);

const { t } = useI18n();
const isOpen = ref(false);

const store = useStore();
const agents = useMapGetter('agents/getAgents');
const currentUser = useMapGetter('getCurrentUser');

const toSellerOption = user => ({
  id: user.id,
  name: user.name || user.email,
});

const sellers = computed(() => {
  const options = agents.value.filter(user => user.id).map(toSellerOption);
  const user = currentUser.value;

  if (user?.id && !options.some(option => option.id === user.id)) {
    return [toSellerOption(user), ...options];
  }

  return options;
});

const ensureAgentsLoaded = () => {
  if (!agents.value.length) store.dispatch('agents/get');
};

const STATUSES = [
  { value: 'draft', labelKey: 'PURCHASE_PROJECTS.STATUSES.DRAFT' },
  { value: 'active', labelKey: 'PURCHASE_PROJECTS.STATUSES.ACTIVE' },
  { value: 'shared', labelKey: 'PURCHASE_PROJECTS.STATUSES.SHARED' },
  { value: 'fulfilled', labelKey: 'PURCHASE_PROJECTS.STATUSES.FULFILLED' },
  { value: 'archived', labelKey: 'PURCHASE_PROJECTS.STATUSES.ARCHIVED' },
];

const OCCASIONS = [
  { value: 'birthday', labelKey: 'PURCHASE_PROJECTS.OCCASIONS.BIRTHDAY' },
  { value: 'wedding', labelKey: 'PURCHASE_PROJECTS.OCCASIONS.WEDDING' },
  { value: 'gift', labelKey: 'PURCHASE_PROJECTS.OCCASIONS.GIFT' },
  { value: 'personal', labelKey: 'PURCHASE_PROJECTS.OCCASIONS.PERSONAL' },
  { value: 'other', labelKey: 'PURCHASE_PROJECTS.OCCASIONS.OTHER' },
];

const form = reactive({
  title: '',
  sellerId: '',
  status: 'draft',
  occasion: '',
  dueDate: '',
  internalNote: '',
  beneficiaryType: 'main_contact',
  beneficiaryId: null,
  beneficiarySnapshot: null,
});

const isFormValid = computed(() => form.title.trim().length > 0);

const contactId = computed(
  () => props.project.contactId ?? props.project.customerId ?? 'cus_001'
);

const beneficiarySelection = computed({
  get: () => ({
    beneficiaryType: form.beneficiaryType,
    beneficiaryId: form.beneficiaryId,
    beneficiarySnapshot: form.beneficiarySnapshot,
  }),
  set: value => {
    form.beneficiaryType = value.beneficiaryType;
    form.beneficiaryId = value.beneficiaryId;
    form.beneficiarySnapshot = value.beneficiarySnapshot;
  },
});

const open = () => {
  ensureAgentsLoaded();
  form.title = props.project.title ?? '';
  form.sellerId = props.project.sellerId ?? sellers.value[0]?.id ?? '';
  form.status = props.project.status ?? 'draft';
  form.occasion = props.project.occasion ?? '';
  form.dueDate = props.project.dueDate ?? '';
  form.internalNote = props.project.internalNote ?? '';
  form.beneficiaryType = props.project.beneficiaryType ?? 'main_contact';
  form.beneficiaryId = props.project.beneficiaryId ?? null;
  form.beneficiarySnapshot = props.project.beneficiarySnapshot ?? null;
  isOpen.value = true;
};

const close = () => {
  isOpen.value = false;
};

const handleSubmit = () => {
  if (!isFormValid.value) return;
  const seller = sellers.value.find(
    s => String(s.id) === String(form.sellerId)
  );
  emit('updated', {
    title: form.title.trim(),
    sellerId: form.sellerId,
    sellerName: seller?.name ?? '',
    status: form.status,
    occasion: form.occasion,
    dueDate: form.dueDate,
    internalNote: form.internalNote,
    beneficiaryType: form.beneficiaryType,
    beneficiaryId: form.beneficiaryId,
    beneficiarySnapshot: form.beneficiarySnapshot,
  });
  close();
};

defineExpose({ open, close });

onMounted(ensureAgentsLoaded);
</script>

<template>
  <Teleport to="body">
    <div
      class="fixed inset-0 z-[70] flex items-end justify-center pointer-events-none transition-colors duration-200 ease-in-out"
      :class="
        isOpen
          ? 'pointer-events-auto bg-n-alpha-black1 backdrop-blur-[4px]'
          : 'bg-transparent'
      "
      @click.self="close"
    >
      <div
        class="w-full sm:max-w-xl h-[88vh] max-h-[88vh] bg-n-solid-2 rounded-t-[1.5rem] sm:rounded-t-2xl shadow-xl flex flex-col overflow-hidden transition-transform duration-200 ease-in-out"
        :class="isOpen ? 'translate-y-0' : 'translate-y-full'"
      >
        <!-- Header -->
        <div class="flex items-center gap-3 px-4 py-3 shrink-0 bg-n-solid-1">
          <Button
            variant="ghost"
            color="slate"
            size="sm"
            icon="i-lucide-x"
            class="!rounded-full bg-n-alpha-black2 shrink-0"
            @click="close"
          />
          <h3 class="text-base font-medium text-n-slate-12 truncate">
            {{ t('PURCHASE_PROJECTS.FORM.EDIT_TITLE') }}
          </h3>
        </div>

        <!-- Scrollable content -->
        <div class="flex-1 overflow-y-auto overflow-x-hidden min-h-0">
          <div class="bg-n-alpha-1 flex flex-col gap-2 py-2">
            <div class="bg-n-solid-1 px-4 pt-3 pb-4">
              <div class="flex flex-col gap-3">
                <!-- Nom du projet -->
                <div class="flex flex-col gap-1">
                  <label class="text-xs font-medium text-n-slate-10">
                    {{ t('PURCHASE_PROJECTS.FORM.PROJECT_NAME_LABEL') }}
                    <span class="text-red-500">*</span>
                  </label>
                  <input
                    v-model="form.title"
                    type="text"
                    class="no-margin w-full m-0 rounded-lg bg-n-alpha-1 px-3 py-2 text-sm text-n-slate-12 placeholder:text-n-slate-10 focus:outline-none"
                    :placeholder="
                      t('PURCHASE_PROJECTS.FORM.PROJECT_NAME_PLACEHOLDER')
                    "
                    @keydown.enter="handleSubmit"
                  />
                </div>

                <div class="flex flex-col gap-3">
                  <span class="text-sm font-semibold text-n-slate-12">
                    {{ t('CLOSE_CIRCLE.BENEFICIARY.TITLE') }}
                  </span>
                  <CloseCircleBeneficiaryPicker
                    v-model="beneficiarySelection"
                    :contact-id="contactId"
                  />
                </div>

                <!-- Statut + Vendeur -->
                <div class="grid grid-cols-2 gap-3">
                  <div class="flex flex-col gap-1">
                    <label class="text-xs font-medium text-n-slate-10">
                      {{ t('PURCHASE_PROJECTS.FORM.STATUS_LABEL') }}
                    </label>
                    <div class="relative">
                      <select
                        v-model="form.status"
                        class="block w-full m-0 h-9 appearance-none bg-none border-0 rounded-lg bg-n-alpha-1 px-3 py-2 pr-8 text-sm text-n-slate-12 focus:outline-none"
                      >
                        <option
                          v-for="s in STATUSES"
                          :key="s.value"
                          :value="s.value"
                        >
                          {{ t(s.labelKey) }}
                        </option>
                      </select>
                      <span
                        class="i-lucide-chevron-down size-3 text-n-slate-10 pointer-events-none absolute right-3 top-1/2 -translate-y-1/2"
                      />
                    </div>
                  </div>

                  <div class="flex flex-col gap-1">
                    <label class="text-xs font-medium text-n-slate-10">
                      {{ t('PURCHASE_PROJECTS.FORM.SELLER_LABEL') }}
                    </label>
                    <div class="relative">
                      <select
                        v-model="form.sellerId"
                        class="block w-full m-0 h-9 appearance-none bg-none border-0 rounded-lg bg-n-alpha-1 px-3 py-2 pr-8 text-sm text-n-slate-12 focus:outline-none"
                      >
                        <option
                          v-for="seller in sellers"
                          :key="seller.id"
                          :value="seller.id"
                        >
                          {{ seller.name }}
                        </option>
                      </select>
                      <span
                        class="i-lucide-chevron-down size-3 text-n-slate-10 pointer-events-none absolute right-3 top-1/2 -translate-y-1/2"
                      />
                    </div>
                  </div>
                </div>

                <!-- Occasion + Échéance -->
                <div class="grid grid-cols-2 gap-3">
                  <div class="flex flex-col gap-1">
                    <label class="text-xs font-medium text-n-slate-10">
                      {{ t('PURCHASE_PROJECTS.FORM.OCCASION_LABEL') }}
                    </label>
                    <div class="relative">
                      <select
                        v-model="form.occasion"
                        class="block w-full m-0 h-9 appearance-none bg-none border-0 rounded-lg bg-n-alpha-1 px-3 py-2 pr-8 text-sm text-n-slate-12 focus:outline-none"
                      >
                        <option value="">—</option>
                        <option
                          v-for="occ in OCCASIONS"
                          :key="occ.value"
                          :value="occ.value"
                        >
                          {{ t(occ.labelKey) }}
                        </option>
                      </select>
                      <span
                        class="i-lucide-chevron-down size-3 text-n-slate-10 pointer-events-none absolute right-3 top-1/2 -translate-y-1/2"
                      />
                    </div>
                  </div>

                  <div class="flex flex-col gap-1">
                    <label class="text-xs font-medium text-n-slate-10">
                      {{ t('PURCHASE_PROJECTS.FORM.DUE_DATE_LABEL') }}
                    </label>
                    <input
                      v-model="form.dueDate"
                      type="date"
                      class="no-margin w-full m-0 h-9 rounded-lg bg-n-alpha-1 px-3 py-2 text-sm text-n-slate-12 focus:outline-none appearance-none"
                    />
                  </div>
                </div>

                <!-- Note interne -->
                <div class="flex flex-col gap-1">
                  <label class="text-xs font-medium text-n-slate-10">
                    {{ t('PURCHASE_PROJECTS.FORM.INTERNAL_NOTE_LABEL') }}
                  </label>
                  <textarea
                    v-model="form.internalNote"
                    rows="3"
                    class="w-full m-0 rounded-lg bg-n-alpha-1 px-3 py-2 text-sm text-n-slate-12 placeholder:text-n-slate-10 focus:outline-none resize-none"
                    :placeholder="
                      t('PURCHASE_PROJECTS.FORM.INTERNAL_NOTE_PLACEHOLDER')
                    "
                  />
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Footer -->
        <div
          class="px-4 pb-4 pt-3 bg-n-solid-1 border-t border-n-strong shrink-0 flex gap-2"
        >
          <Button
            variant="outline"
            color="slate"
            size="md"
            class="flex-1"
            :label="t('PURCHASE_PROJECTS.FORM.CANCEL')"
            @click="close"
          />
          <Button
            variant="solid"
            color="blue"
            size="md"
            class="flex-1"
            :label="t('PURCHASE_PROJECTS.FORM.SAVE')"
            :disabled="!isFormValid"
            @click="handleSubmit"
          />
        </div>
      </div>
    </div>
  </Teleport>
</template>
